/**
 * Formulas for derived analytes.
 *
 * A derived entry may carry a `formula`: a small arithmetic expression over
 * the entry's declared inputs and the definition's constants, so the lab can
 * read and audit how a value is computed and a new assay needs no code for
 * it. Kept deliberately small: numbers, identifiers, `+ - * /`, unary minus
 * and parentheses. No functions and no conditionals — rounding comes from
 * the entry's `decimals`, a missing input yields no value, and so does a
 * division by zero.
 *
 * One evaluator, used wherever a test is written (the Capital OS save rule,
 * the cross-test copy job, eventually the LIMS write path itself) and by the
 * sheet for its live preview, so every consumer computes the same number.
 */
export const MAX_FORMULA_LENGTH = 500;
const IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_]*/;
const NUMBER = /^(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/;
function tokenize(src) {
    const tokens = [];
    let i = 0;
    while (i < src.length) {
        const c = src[i];
        if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
            i++;
            continue;
        }
        if (c === '+' || c === '-' || c === '*' || c === '/') {
            tokens.push({ type: 'operator', value: c, at: i });
            i++;
            continue;
        }
        if (c === '(' || c === ')') {
            tokens.push({ type: 'paren', value: c, at: i });
            i++;
            continue;
        }
        const rest = src.slice(i);
        const num = NUMBER.exec(rest);
        if (num) {
            const value = Number(num[0]);
            if (!Number.isFinite(value))
                return `invalid number "${num[0]}" at ${i}`;
            tokens.push({ type: 'number', value, at: i });
            i += num[0].length;
            continue;
        }
        const id = IDENTIFIER.exec(rest);
        if (id) {
            tokens.push({ type: 'identifier', name: id[0], at: i });
            i += id[0].length;
            continue;
        }
        return `unexpected character "${c}" at ${i}`;
    }
    tokens.push({ type: 'end', at: src.length });
    return tokens;
}
/**
 * Parse a formula. Errors name the position so a definition editor can point
 * at the problem.
 */
export function parseFormula(src) {
    if (typeof src !== 'string' || src.trim().length === 0)
        return { ok: false, error: 'formula: must be a non-empty string' };
    if (src.length > MAX_FORMULA_LENGTH)
        return { ok: false, error: `formula: must be at most ${MAX_FORMULA_LENGTH} characters` };
    const tokens = tokenize(src);
    if (typeof tokens === 'string')
        return { ok: false, error: `formula: ${tokens}` };
    let pos = 0;
    const peek = () => tokens[pos];
    const next = () => tokens[pos++];
    const identifiers = [];
    const fail = (message) => { throw new Error(message); };
    const describe = (t) => t.type === 'end' ? 'end of formula'
        : t.type === 'number' ? `number ${t.value}`
            : t.type === 'identifier' ? `"${t.name}"`
                : `"${t.value}"`;
    function primary() {
        const t = next();
        if (t.type === 'number')
            return { type: 'number', value: t.value };
        if (t.type === 'identifier') {
            if (!identifiers.includes(t.name))
                identifiers.push(t.name);
            return { type: 'identifier', name: t.name };
        }
        if (t.type === 'paren' && t.value === '(') {
            const inner = expression();
            const close = next();
            if (close.type !== 'paren' || close.value !== ')')
                return fail(`expected ")" at ${close.at}, got ${describe(close)}`);
            return inner;
        }
        return fail(`expected a number, name or "(" at ${t.at}, got ${describe(t)}`);
    }
    function unary() {
        const t = peek();
        if (t.type === 'operator' && t.value === '-') {
            next();
            return { type: 'unary', operator: '-', operand: unary() };
        }
        if (t.type === 'operator' && t.value === '+') {
            next();
            return unary();
        }
        return primary();
    }
    function term() {
        let left = unary();
        for (;;) {
            const t = peek();
            if (t.type === 'operator' && (t.value === '*' || t.value === '/')) {
                next();
                left = { type: 'binary', operator: t.value, left, right: unary() };
            }
            else {
                return left;
            }
        }
    }
    function expression() {
        let left = term();
        for (;;) {
            const t = peek();
            if (t.type === 'operator' && (t.value === '+' || t.value === '-')) {
                next();
                left = { type: 'binary', operator: t.value, left, right: term() };
            }
            else {
                return left;
            }
        }
    }
    try {
        const ast = expression();
        const trailing = next();
        if (trailing.type !== 'end')
            fail(`unexpected ${describe(trailing)} at ${trailing.at}`);
        return { ok: true, formula: { ast, identifiers } };
    }
    catch (e) {
        return { ok: false, error: `formula: ${e.message}` };
    }
}
/**
 * Evaluate a parsed formula. `resolve` returns the value of an identifier or
 * null when it has none; any null operand, and any non-finite result
 * (division by zero), yields null — the derived value stays blank.
 */
export function evaluateFormula(formula, resolve) {
    const root = 'ast' in formula ? formula.ast : formula;
    const walk = (node) => {
        switch (node.type) {
            case 'number': return node.value;
            case 'identifier': {
                const v = resolve(node.name);
                return typeof v === 'number' && Number.isFinite(v) ? v : null;
            }
            case 'unary': {
                const v = walk(node.operand);
                return v === null ? null : -v;
            }
            case 'binary': {
                const l = walk(node.left);
                if (l === null)
                    return null;
                const r = walk(node.right);
                if (r === null)
                    return null;
                switch (node.operator) {
                    case '+': return l + r;
                    case '-': return l - r;
                    case '*': return l * r;
                    case '/': return r === 0 ? null : l / r;
                }
            }
        }
    };
    const result = walk(root);
    return result !== null && Number.isFinite(result) ? result : null;
}
/**
 * Round to `decimals` places with ties toward +∞ (`Math.round`), the same
 * rounding the labos builders apply (omni, fe3), so a formula reproduces the
 * stored values of the assays it replaces. Note the asymmetry on negative
 * ties: -2.5 rounds to -2.
 */
export function roundTo(value, decimals) {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
}
